import { UploadFolder } from "@/services/filesService";

export type ImageStandardKey = "category" | "product" | "storeLogo" | "storeBanner" | "homeBanner";

export interface ImageSpec {
  key: ImageStandardKey;
  width: number;
  height: number;
  minWidth: number;
  minHeight: number;
  /** How to fit source into target canvas before upload */
  fit: "contain" | "cover";
  background: string;
  label: string;
  hint: string;
  aspectRatio: string;
}

export const IMAGE_SPECS: Record<ImageStandardKey, ImageSpec> = {
  category: {
    key: "category",
    width: 512,
    height: 512,
    minWidth: 200,
    minHeight: 200,
    fit: "contain",
    background: "#ffffff",
    label: "512 × 512 px",
    hint: "Square category image. Full image is preserved with padding if needed.",
    aspectRatio: "1 / 1",
  },
  product: {
    key: "product",
    width: 1200,
    height: 1200,
    minWidth: 600,
    minHeight: 600,
    fit: "cover",
    background: "#ffffff",
    label: "1200 × 1200 px",
    hint: "Square product photo. Center-cropped to standard size on upload.",
    aspectRatio: "1 / 1",
  },
  storeLogo: {
    key: "storeLogo",
    width: 512,
    height: 512,
    minWidth: 200,
    minHeight: 200,
    fit: "contain",
    background: "#ffffff",
    label: "512 × 512 px",
    hint: "Square store logo. Full logo preserved with padding if needed.",
    aspectRatio: "1 / 1",
  },
  storeBanner: {
    key: "storeBanner",
    width: 1200,
    height: 400,
    minWidth: 600,
    minHeight: 200,
    fit: "cover",
    background: "#ffffff",
    label: "1200 × 400 px",
    hint: "Wide banner (3:1). Center-cropped to standard size on upload.",
    aspectRatio: "3 / 1",
  },
  homeBanner: {
    key: "homeBanner",
    width: 1024,
    height: 1024,
    minWidth: 500,
    minHeight: 500,
    fit: "cover",
    background: "#ffffff",
    label: "1024 × 1024 px",
    hint: "Square homepage banner. Center-cropped to square format on upload.",
    aspectRatio: "1 / 1",
  },
};

export function specForFolder(folder: UploadFolder, variant?: "logo" | "banner"): ImageSpec {
  switch (folder) {
    case UploadFolder.Categories:
      return IMAGE_SPECS.category;
    case UploadFolder.Products:
      return IMAGE_SPECS.product;
    case UploadFolder.Stores:
      return variant === "banner" ? IMAGE_SPECS.storeBanner : IMAGE_SPECS.storeLogo;
    case UploadFolder.Banners:
      return IMAGE_SPECS.homeBanner;
    default:
      return IMAGE_SPECS.product;
  }
}

export function isSquareSpec(spec: ImageSpec): boolean {
  return spec.width === spec.height;
}
